import { useCallback, useEffect, useState } from "react";
import {
  GUC_ADI,
  GUVENILIR_CIHAZ_GUN,
  OGRENCI_EN_AZ,
  YEDEK_KOD_SAYISI,
  sifreGucu,
} from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, SifreDegistirFormu } from "@koclukpanel/ui";
import { zorunlulukCumlesi } from "./iki-asama-zorunluluk";
import {
  ApiHatasi,
  digerOturumlariSonlandir,
  guvenilirCihazKaldir,
  guvenilirCihazlar,
  guvenilirCihazlariKaldir,
  ikiAsamaDurum,
  ikiAsamaKapat,
  ikiAsamaKurulumBaslat,
  ikiAsamaKurulumTamamla,
  oturumlarim,
  oturumSonlandir,
  sifreDegistir,
  yedekKodlariYenile,
  type GuvenilirCihaz,
  type IkiAsamaDurumu,
  type OturumOzeti,
} from "./api";

/**
 * GUVENLIK — ISTEGE BAGLI IKI ADIMLI DOGRULAMA (G-AUT-01)
 *
 * ================================================================
 * POLITIKA SUNUCUDAN, VARSAYIMDAN DEGIL
 * ================================================================
 * Ekran "ogrenciyse istege baglidir" diye varsaymiyor; `etkinPolitika`yi
 * sunucudan aliyor. Varsaysaydi, ileride bir ogrenci rolune zorunluluk
 * getirildiginde ekran hala kapatma dugmesi gosterir, sunucu reddeder
 * ve kullanici icin "bozuk dugme" olurdu.
 *
 * ================================================================
 * KAPATMA DA KOD ISTIYOR
 * ================================================================
 * 2FA'nin korudugu sey tam olarak "sifreyi bilen ama telefonu
 * olmayan" birinin girmesi. Acik bir oturumu ele geciren biri kodsuz
 * kapatabilseydi koruma yalnizca ilk giriste var olurdu. Bu, EKRAN
 * kurali degil sunucu kurali — burada yalnizca aciklaniyor.
 *
 * ================================================================
 * "TELEFONUMU KAYBEDERSEM" SORUSU ONCEDEN CEVAPLANIYOR
 * ================================================================
 * Ogrencilerin cogu resit degil ve telefon degistirmek onlarda
 * siradan bir olay. Yedek kodlarin ne ise yaradigi ve kaybedilirse ne
 * yapilacagi ACMADAN ONCE yaziliyor — sonra yazsaydik, kilitlenen
 * ogrenci o metni okuyabilecegi ekrana zaten giremezdi.
 */

/**
 * ================================================================
 * KART DİLİ — ÖĞRENCİ UYGULAMASININ GERİ KALANIYLA AYNI
 * ================================================================
 * Bu kutu tek başına `rounded-alan` ve gölgesiz duruyordu; oysa
 * `GorevKarti`, `HataDefterim` ve `DegisiklikTalebi` aynı ekranlarda
 * `rounded-kart` + `shadow-kart` kullanıyor. Çizim
 * (`OgrenciGuvenlikMasaustu.dc.html`) de kartları `--radius-kart` ve
 * `--shadow-kart` ile veriyor.
 *
 * Fark küçük ama tek bir ekranda iki farklı kart dili, ekranın "başka
 * bir yerden yapıştırılmış" gibi okunmasına yetiyor — hele o ekran
 * güvenlik ekranıysa.
 */
function Kutu({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4">{children}</div>
  );
}

/**
 * Çizimdeki alıntı çubuklu bilgi bloğu.
 *
 * Düz bir paragraf yerine bu kullanılıyor çünkü söylediği şey kartın
 * asıl konusu DEĞİL, ona bağlı bir hatırlatma: "2FA açık" bilgisiyle
 * "kaç yedek kodun kaldı" bilgisi aynı tipografide durunca ikisi de
 * okunmuyor. Çizimdeki karşılığı: `--color-notr-50` zemin, 3px'lik
 * `--color-notr-400` çubuk, 12.5px metin.
 */
function BilgiSatiri({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-alan bg-notr-50 mt-3 flex gap-2.5 px-3.5 py-3">
      <div className="bg-notr-400 w-[3px] shrink-0 rounded-full" aria-hidden="true" />
      <p className="text-notr-700 text-[12.5px] leading-relaxed">{children}</p>
    </div>
  );
}

/**
 * ŞİFRE DEĞİŞTİRME — G-AUT-08a (A9, 05.10.2026). Öğrenci ve veli için;
 * form panel ile ORTAK (`@koclukpanel/ui`). Asgari uzunluk öğrenci/veli
 * hesabının (8); kararı yine sunucu veriyor.
 */
function SifreDegistir() {
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [basari, setBasari] = useState<string | null>(null);
  return (
    <Kutu>
      <h3 className="text-notr-900 text-sm font-semibold">Şifre</h3>
      <p className="text-notr-600 mt-1 mb-3 text-sm">
        Mevcut şifrenle yeni bir şifre belirle. Unuttuysan giriş ekranındaki “Şifremi unuttum”u
        kullan.
      </p>
      <SifreDegistirFormu
        gucHesapla={(s) => sifreGucu(s, OGRENCI_EN_AZ)}
        gucAdi={GUC_ADI}
        enAz={OGRENCI_EN_AZ}
        gonderiliyor={calisiyor}
        hata={hata}
        basari={basari}
        onGonder={(v) => {
          setCalisiyor(true);
          setHata(null);
          setBasari(null);
          void sifreDegistir(v)
            .then((r) =>
              setBasari(
                r.kapatilanOturum > 0
                  ? `Şifren değiştirildi. Diğer cihazlardaki ${r.kapatilanOturum} oturum kapatıldı.`
                  : "Şifren değiştirildi.",
              ),
            )
            .catch((e: unknown) =>
              setHata(e instanceof ApiHatasi ? e.message : "Şifre değiştirilemedi."),
            )
            .finally(() => setCalisiyor(false));
        }}
      />
    </Kutu>
  );
}

function YedekKodlar({ kodlar, onKapat }: { kodlar: string[]; onKapat: () => void }) {
  return (
    <Kutu>
      <h3 className="text-notr-900 text-sm font-semibold">Yedek kodların</h3>
      <p className="text-notr-600 mt-1 text-sm">
        Bunları <strong>bir kez</strong> görüyorsun. Telefonunu kaybedersen giriş yapmanın tek yolu
        bunlar. Ekran görüntüsü alma — bir kâğıda yaz ve sakla.
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-2">
        {kodlar.map((k) => (
          <li key={k} className="bg-notr-100 rounded px-2 py-1.5 text-center font-mono text-sm">
            {k}
          </li>
        ))}
      </ul>
      <Buton boyut="genis" onClick={onKapat} className="mt-4">
        Yazdım, kapat
      </Buton>
    </Kutu>
  );
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
}

function tarihSaat(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * GÜVENİLİR CİHAZLAR — G-AUT-01c kural 6
 *
 * ================================================================
 * KUTU VARDI, KALDIRACAK EKRAN YOKTU
 * ================================================================
 * Giriş ekranında "Bu cihazı {GUVENILIR_CIHAZ_GUN} gün hatırla" kutusu
 * çalışıyordu ama öğrencinin işaretlediği cihazı GÖRECEĞİ ya da
 * KALDIRACAĞI hiçbir yer yoktu — panelde vardı, burada yoktu.
 *
 * Kural 6'nın gerekçesi doğrudan bu: "kaybettiği cihazı
 * kapatabilmeli." Telefonunu kaybeden öğrencinin elindeki tek yol
 * şifresini sıfırlatmak olurdu ve bunu bilmesinin bir yolu yoktu.
 *
 * ================================================================
 * TABLO DEĞİL KART — TELEFON EKRANI
 * ================================================================
 * Paneldeki karşılığı (`Hesabim.tsx`) altı sütunlu bir tablo. Burada
 * ekran 390px: her cihaz kendi kartında, ad üstte, "nereden ve ne
 * zaman" altta, düğme sağda. Yatay kaydırma YOK — kaydırılan bir
 * listede görünmeyen cihazın var olduğu da gizlenir.
 *
 * ================================================================
 * YALNIZCA 2FA AÇIKKEN ÇİZİLİYOR
 * ================================================================
 * Paneldeki bölüm hiç cihaz yokken de çiziliyor ("boş liste 'hayır'
 * demek"). Burada çizilmiyor ve sebebi mantıksal: güvenilir cihaz
 * ancak ikinci aşamada oluşuyor ve 2FA kapatılınca sunucu hepsini
 * düşürüyor. Yani 2FA kapalıyken liste KANITLANABİLİR biçimde boş —
 * "kod sorulmadan girilen bir yer var mı" sorusunun cevabı zaten
 * hayır, çünkü sorulan bir kod yok.
 */
function GuvenilirCihazlar() {
  const [liste, setListe] = useState<GuvenilirCihaz[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /** Hangi satırın işlemi sürüyor — cihaz kimliği ya da "hepsi". */
  const [calisan, setCalisan] = useState<string | null>(null);
  const [kaldirilan, setKaldirilan] = useState<number | null>(null);

  const yukle = useCallback(async () => {
    try {
      setListe(await guvenilirCihazlar());
      setHata(null);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Cihazların yüklenemedi.");
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  async function kaldir(cihazId: string): Promise<void> {
    setCalisan(cihazId);
    setKaldirilan(null);
    try {
      await guvenilirCihazKaldir(cihazId);
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Cihaz kaldırılamadı.");
    } finally {
      setCalisan(null);
    }
  }

  async function hepsiniKaldir(): Promise<void> {
    setCalisan("hepsi");
    try {
      const { kaldirilan: adet } = await guvenilirCihazlariKaldir();
      setKaldirilan(adet);
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Cihazlar kaldırılamadı.");
    } finally {
      setCalisan(null);
    }
  }

  return (
    <Kutu>
      {/*
        BAŞLIK `h2` VE 16px — "İki adımlı doğrulama" ile AYNI düzeyde.
        `h3` bıraksaydık ekran okuyucuda ve gözde 2FA kartının alt
        başlığı gibi okunurdu; oysa bu ayrı bir konu: biri "kod
        soruluyor mu", öteki "nerede sorulmuyor".
      */}
      <h2 className="text-notr-900 text-base font-semibold">Güvenilir cihazlar</h2>
      <p className="text-notr-700 mt-1 text-[13.5px] leading-relaxed">
        Girişte <strong>{GUVENILIR_CIHAZ_GUN} gün boyunca</strong> kod sorulmayan tarayıcılar.
        Tanımadığın bir satır görürsen kaldır ve şifreni değiştir.
      </p>

      {hata && (
        <p role="alert" className="text-hata-700 mt-3 text-sm">
          {hata}
        </p>
      )}

      {kaldirilan !== null && (
        <p className="text-notr-600 mt-3 text-sm">
          {kaldirilan} cihaz kaldırıldı. Bir dahaki girişte kod sorulacak.
        </p>
      )}

      {liste === null ? (
        hata === null && <Iskelet satir={2} />
      ) : liste.length === 0 ? (
        <div className="mt-3">
          <BosDurum
            baslik="Güvenilir cihaz yok"
            aciklama="Her girişte doğrulama kodu soruluyor. Bir cihazı hatırlatmak için giriş ekranındaki kutuyu işaretle."
          />
        </div>
      ) : (
        <>
          <ul className="mt-3 flex flex-col gap-2">
            {liste.map((c) => (
              <li
                key={c.id}
                /*
                  Çizimdeki oturum satırı: kenarlık YOK, `notr-50`
                  zemin. Kart zaten kenarlıklı — içine ikinci bir
                  kenarlık koymak satırları kutu içinde kutu yapıyordu.
                */
                className="rounded-alan bg-notr-50 flex items-center gap-3 px-[15px] py-[13px]"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-notr-900 truncate text-sm font-medium">
                    {c.label ?? "Bilinmeyen cihaz"}
                  </p>
                  <p className="text-notr-600 mt-0.5 text-xs">
                    {c.ipAddress ?? "IP bilinmiyor"} ·{" "}
                    {/*
                      "—" degil "hic kullanilmadi": bos bir alan
                      olculmemis mi yoksa sifir mi belli etmiyor. Hic
                      kullanilmamis bir guvenilir cihaz, ogrencinin
                      hatirlamadigi bir isaretleme demek.
                    */}
                    {c.lastUsedAt ? `son giriş ${tarihSaat(c.lastUsedAt)}` : "hiç kullanılmadı"}
                  </p>
                  <p className="text-notr-600 mt-0.5 text-xs">
                    {tarih(c.expiresAt)} tarihinde biter
                  </p>
                </div>
                <Buton
                  gorunum="sessiz"
                  yukleniyor={calisan === c.id}
                  onClick={() => void kaldir(c.id)}
                >
                  Kaldır
                  <span className="sr-only"> — {c.label ?? "bilinmeyen cihaz"}</span>
                </Buton>
              </li>
            ))}
          </ul>

          <Buton
            gorunum="ikincil"
            boyut="genis"
            yukleniyor={calisan === "hepsi"}
            onClick={() => void hepsiniKaldir()}
            className="mt-3"
          >
            Hepsini kaldır
          </Buton>
          <p className="text-notr-600 mt-2 text-xs">
            Şifreni değiştirdiğinde ya da iki adımlı doğrulamayı kapattığında bu cihazların hepsi
            kendiliğinden düşer.
          </p>
        </>
      )}
    </Kutu>
  );
}

/**
 * AÇIK OTURUMLAR — G-SES-03
 *
 * ================================================================
 * "GÜVENİLİR CİHAZLAR"DAN AYRI BÖLÜM
 * ================================================================
 * İkisi de bir cihaz listesi ve tek listede birleştirmek cazip. Ama
 * apayrı iki şey söylüyorlar:
 *
 *   Açık oturum      "şu an nereden GİRİLMİŞ" — kapatınca o cihaz
 *                    dışarı düşer
 *   Güvenilir cihaz  "nerede kod SORULMUYOR" — kaldırınca kimse
 *                    dışarı düşmez, sadece bir dahakine kod sorulur
 *
 * Tek listede olsalardı öğrenci "kapat"a basıp cihazın çıkmasını
 * beklerdi ve çıkmayınca sistemin bozuk olduğunu düşünürdü.
 *
 * ================================================================
 * "BU CİHAZ" İŞARETİ SUNUCUDAN
 * ================================================================
 * Arayüz tahmin EDEMEZ: oturum çerezi `httpOnly`, JavaScript onu
 * okuyamıyor. Tahmin edilseydi öğrenciye kendi oturumunu kapattırmak
 * an meselesiydi — ve kapattığı anda ekran da giderdi.
 */
function AcikOturumlar() {
  const [liste, setListe] = useState<OturumOzeti[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [calisan, setCalisan] = useState<string | null>(null);
  const [kapatilan, setKapatilan] = useState<number | null>(null);

  const yukle = useCallback(async () => {
    try {
      setListe(await oturumlarim());
      setHata(null);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Oturumların yüklenemedi.");
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  async function kapat(id: string): Promise<void> {
    setCalisan(id);
    setKapatilan(null);
    try {
      await oturumSonlandir(id);
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Oturum kapatılamadı.");
    } finally {
      setCalisan(null);
    }
  }

  async function digerleriniKapat(): Promise<void> {
    setCalisan("digerleri");
    try {
      const { kapatilan: adet } = await digerOturumlariSonlandir();
      setKapatilan(adet);
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Oturumlar kapatılamadı.");
    } finally {
      setCalisan(null);
    }
  }

  return (
    <Kutu>
      <h2 className="text-notr-900 text-base font-semibold">Açık oturumlar</h2>
      <p className="text-notr-700 mt-1 text-[13.5px] leading-relaxed">
        Hesabına şu an hangi cihazlardan girilmiş. Tanımadığın bir satır görürsen kapat ve şifreni
        değiştir.
      </p>

      {hata && (
        <p role="alert" className="text-hata-700 mt-3 text-sm">
          {hata}
        </p>
      )}

      {kapatilan !== null && (
        <p className="text-notr-600 mt-3 text-sm">{kapatilan} oturum kapatıldı.</p>
      )}

      {liste === null ? (
        hata === null && <Iskelet satir={2} />
      ) : (
        <>
          <ul className="mt-3 flex flex-col gap-2">
            {liste.map((o) => (
              <li
                key={o.id}
                className="rounded-alan bg-notr-50 flex items-center gap-3 px-[15px] py-[13px]"
              >
                <div className="min-w-0 flex-1">
                  {/*
                    ================================================
                    `platform`A DÜŞMÜYOR — O SÜTUN BİR SABİT
                    ================================================
                    Ölçüldü (04.09.2026, 774 oturum): `platform`ın
                    veritabanındaki BÜTÜN satırlarda tek bir değeri
                    var — "web". Ölçüm gibi duran ama hiç değişmeyen
                    bir sütun. Ona düşen bir yedek, öğrenciye cihaz adı
                    diye "web" yazardı: hangi cihaz olduğunu söylemeyen,
                    ama cihaz adı gibi okunan bir kelime. Bu listenin
                    tek işi "bu ben miyim" sorusunu cevaplatmak.

                    Aynı ölçüm `platform`ın hiç boş olmadığını da
                    gösterdi, yani "Bilinmeyen cihaz" dalına ASLA
                    ulaşılamıyordu — yazılmış ama erişilemez bir yedek.
                  */}
                  <p className="text-notr-900 truncate text-sm font-medium">
                    {o.deviceLabel ?? "Bilinmeyen cihaz"}
                    {o.suAnki && (
                      <span className="rounded-rozet bg-onay-600/10 text-onay-700 ml-2 px-2 py-0.5 text-[11.5px] font-semibold">
                        bu cihaz
                      </span>
                    )}
                  </p>
                  <p className="text-notr-600 mt-0.5 text-xs">
                    {o.ipAddress ?? "IP bilinmiyor"} · son görülme {tarihSaat(o.lastSeenAt)}
                  </p>
                </div>
                {/*
                  KENDİ OTURUMUNDA "Kapat" ÇİZİLMİYOR. Sunucu izin
                  veriyor ama basan öğrenci o anda dışarı düşer ve
                  bunu bir hata sanır — çıkış yapmak istiyorsa alt
                  şeritteki "Çıkış" düğmesi zaten var ve ne yaptığını
                  söylüyor.
                */}
                {!o.suAnki && (
                  <Buton
                    gorunum="sessiz"
                    yukleniyor={calisan === o.id}
                    onClick={() => void kapat(o.id)}
                  >
                    Kapat
                    <span className="sr-only"> — {o.deviceLabel ?? "bilinmeyen cihaz"}</span>
                  </Buton>
                )}
              </li>
            ))}
          </ul>

          {/*
            "Diğerlerini kapat" YALNIZCA BİRDEN FAZLA OTURUM VARKEN.
            Tek oturumda düğme hiçbir şey yapmaz; çizip sonra "0 oturum
            kapatıldı" demek, öğrenciye bozuk bir düğme göstermek olurdu.
          */}
          {liste.length > 1 && (
            <Buton
              gorunum="ikincil"
              boyut="genis"
              yukleniyor={calisan === "digerleri"}
              onClick={() => void digerleriniKapat()}
              className="mt-3"
            >
              Bu cihaz hariç hepsini kapat
            </Buton>
          )}
        </>
      )}
    </Kutu>
  );
}

export function Guvenlik() {
  const [durum, setDurum] = useState<IkiAsamaDurumu | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [calisiyor, setCalisiyor] = useState(false);

  const [kurulum, setKurulum] = useState<{ gizliAnahtar: string; qrVeriUrl: string } | null>(null);
  const [anahtarKopyalandi, setAnahtarKopyalandi] = useState(false);

  /** Panoya kopyalama — `Takvimim`deki ICS adresiyle aynı kalıp. */
  const anahtariKopyala = async (anahtar: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(anahtar);
      setAnahtarKopyalandi(true);
    } catch {
      // Pano izni yoksa anahtar zaten ekranda duruyor; sessiz kalmıyoruz.
      setHata("Kopyalanamadı; anahtarı elle seçip kopyalayabilirsin.");
    }
  };
  const [kod, setKod] = useState("");
  const [yedekKodlar, setYedekKodlar] = useState<string[] | null>(null);
  const [kapatmaAcik, setKapatmaAcik] = useState(false);

  const yukle = useCallback(async () => {
    try {
      setDurum(await ikiAsamaDurum());
    } catch {
      setHata("Güvenlik ayarların yüklenemedi.");
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  function hatayiYaz(err: unknown, varsayilan: string): void {
    setHata(err instanceof ApiHatasi ? err.message : varsayilan);
  }

  async function baslat(): Promise<void> {
    setHata(null);
    setCalisiyor(true);
    try {
      const k = await ikiAsamaKurulumBaslat();
      setKurulum({ gizliAnahtar: k.gizliAnahtar, qrVeriUrl: k.qrVeriUrl });
    } catch (err) {
      hatayiYaz(err, "Kurulum başlatılamadı.");
    } finally {
      setCalisiyor(false);
    }
  }

  async function tamamla(): Promise<void> {
    setHata(null);
    setCalisiyor(true);
    try {
      const { yedekKodlar: y } = await ikiAsamaKurulumTamamla(kod.trim());
      setYedekKodlar(y);
      setKurulum(null);
      setKod("");
      await yukle();
    } catch (err) {
      hatayiYaz(err, "Kod doğrulanamadı.");
      setKod("");
    } finally {
      setCalisiyor(false);
    }
  }

  async function kapat(): Promise<void> {
    setHata(null);
    setCalisiyor(true);
    try {
      await ikiAsamaKapat(kod.trim());
      setKapatmaAcik(false);
      setKod("");
      await yukle();
    } catch (err) {
      hatayiYaz(err, "Kapatılamadı.");
      setKod("");
    } finally {
      setCalisiyor(false);
    }
  }

  async function kodlariYenile(): Promise<void> {
    setHata(null);
    setCalisiyor(true);
    try {
      const { yedekKodlar: y } = await yedekKodlariYenile(kod.trim());
      setYedekKodlar(y);
      setKod("");
      await yukle();
    } catch (err) {
      hatayiYaz(err, "Kodlar yenilenemedi.");
      setKod("");
    } finally {
      setCalisiyor(false);
    }
  }

  if (durum === null) {
    // Hata VARSA iskelet degil hata gosteriliyor: iskelet "birazdan
    // gelecek" demek ve gelmeyecek bir seyi beklettirmek yanlis.
    return hata !== null ? (
      <p className="text-hata-500 p-4 text-sm">{hata}</p>
    ) : (
      <div className="p-4">
        <Iskelet satir={3} baslikli />
      </div>
    );
  }

  const kapatilabilir = durum.kuruldu && durum.etkinPolitika !== "REQUIRED";
  const zorunluluk = zorunlulukCumlesi(durum);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-notr-900 text-base font-semibold">İki adımlı doğrulama</h2>
        <p className="text-notr-600 mt-1 text-sm">
          Açarsan, şifreni bilen biri bile telefonundaki koda ulaşmadan hesabına giremez.
        </p>
      </div>

      {hata && (
        <div
          role="alert"
          className="rounded-alan bg-hata-500/10 text-hata-700 flex items-start gap-2 px-3 py-3 text-sm"
        >
          <span aria-hidden="true">⚠</span>
          <span>{hata}</span>
        </div>
      )}

      {yedekKodlar !== null ? (
        <YedekKodlar kodlar={yedekKodlar} onKapat={() => setYedekKodlar(null)} />
      ) : kurulum !== null ? (
        <Kutu>
          {/*
            ================================================================
            ANAHTAR ÖNCE, KAREKOD SONRA — TUVALDEN GELEN DÜZELTME
            ================================================================
            `OgrenciKalan.dc.html` bu ekranı çizerken şunu yazıyor:
            *"TELEFONDA QR İŞE YARAMAZ: kendi ekranını okutamaz"* ve
            çizimde karekod HİÇ yok, yerine kopyalanabilir bir kurulum
            anahtarı var.

            Uygulama tersini yapıyordu: karekod büyük ve birincil
            ("bu kareyi okut"), anahtar altta küçük gri yazıda ve
            **kopyalama düğmesi yoktu**. Bu, TELEFON uygulamasında
            birincil yolu telefonda ÇALIŞMAYAN yol yapıyor — öğrenci
            kendi ekranındaki kareyi kendi telefonuyla okutamaz.

            Üstelik alttaki cümle durumu yanlış anlatıyordu: "karekod
            okunmuyorsa" değil, telefonda okutmak zaten mümkün değil.

            KAREKOD SİLİNMEDİ, ikinci sıraya alındı: bu uygulama duyarlı
            ve öğrenci dizüstünden de açabiliyor; orada karekod gerçekten
            en kısa yol. Çizimin GEREKÇESİ tutuldu, harfi tutulmadı —
            sapma raporda yazılı (C14.17), Emre aksini söylerse karekod
            tamamen kalkar.
          */}
          <h3 className="text-notr-900 text-sm font-semibold">1. Kurulum anahtarını kopyala</h3>
          <p className="text-notr-600 mt-1 text-sm">
            Google Authenticator ya da benzeri bir uygulamada{" "}
            <strong>&ldquo;kurulum anahtarı gir&rdquo;</strong> deyip yapıştır.
          </p>
          <div className="rounded-alan bg-notr-50 ring-notr-200 mt-2 flex items-center gap-2 p-2 ring-1 ring-inset">
            <code className="text-notr-900 min-w-0 flex-1 font-mono text-sm break-all">
              {kurulum.gizliAnahtar}
            </code>
            <Buton onClick={() => void anahtariKopyala(kurulum.gizliAnahtar)}>
              {anahtarKopyalandi ? "Kopyalandı" : "Kopyala"}
            </Buton>
          </div>

          <details className="mt-3">
            {/*
              Karekod KATLANMIŞ: telefonda işe yaramadığı için varsayılan
              olarak yer kaplamamalı, ama dizüstünden açan öğrenciye de
              kapatılmamalı.
            */}
            <summary className="text-notr-600 min-h-dokunma flex cursor-pointer items-center text-sm">
              Başka bir cihazdan okutacaksan karekodu göster
            </summary>
            <img
              src={kurulum.qrVeriUrl}
              alt="Kurulum karekodu"
              className="bg-notr-0 mx-auto mt-2 h-48 w-48 rounded"
            />
            <p className="text-notr-600 mt-1 text-center text-xs">
              Telefonundaki uygulama kendi ekranını okutamaz; karekod ancak başka bir cihazda işe
              yarar.
            </p>
          </details>

          <h3 className="text-notr-900 mt-5 text-sm font-semibold">2. Kodu gir</h3>
          <div className="mt-2 flex flex-col gap-3">
            <Alan
              etiket="Uygulamadaki 6 haneli kod"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={kod}
              onChange={(e) => setKod(e.target.value)}
            />
            <Buton boyut="genis" yukleniyor={calisiyor} onClick={() => void tamamla()}>
              Doğrula ve aç
            </Buton>
            <Buton
              gorunum="sessiz"
              onClick={() => {
                setKurulum(null);
                setKod("");
                setHata(null);
              }}
            >
              Vazgeç
            </Buton>
          </div>
        </Kutu>
      ) : durum.kuruldu ? (
        <Kutu>
          <p className="text-onay-600 text-sm font-medium">İki adımlı doğrulama açık.</p>
          <p className="text-notr-700 mt-1 text-[13.5px] leading-relaxed">
            Girişte telefonundaki uygulamadan{" "}
            <strong className="text-notr-900">6 haneli kod</strong> istenir.
          </p>
          {/*
            KALAN KOD SAYISI ÇİZİMDEKİ BİÇİMDE: "8 / 10", düz bir sayı
            değil. Payda olmadan "8" bir ölçek vermiyor — öğrenci sekizin
            çok mu az mı olduğunu bilemiyor. Payda `YEDEK_KOD_SAYISI`den
            geliyor; elle yazılsaydı sabit değiştiği gün sessizce
            yalan söylerdi.
          */}
          <BilgiSatiri>
            Telefonunu kaybedersen <strong className="text-notr-900">kurtarma kodların</strong> var.
            Kalan: {durum.kalanYedekKod} / {YEDEK_KOD_SAYISI}.
            {durum.kalanYedekKod <= 2 && (
              /*
               * Ikiye dusunce UYARIYOR. "Bitince haber veririz" demek,
               * telefonunu kaybeden ogrencinin elinde hicbir sey
               * kalmadigi anda haber vermek olurdu.
               */
              <span className="text-hata-700 font-medium"> — azaldı, yenilemelisin.</span>
            )}
          </BilgiSatiri>

          <div className="mt-4 flex flex-col gap-3">
            <Alan
              etiket="Uygulamadaki 6 haneli kod"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={kod}
              onChange={(e) => setKod(e.target.value)}
              yardim="Aşağıdaki işlemlerin ikisi de kod ister."
            />
            <Buton gorunum="ikincil" yukleniyor={calisiyor} onClick={() => void kodlariYenile()}>
              Yedek kodları yenile
            </Buton>

            {kapatilabilir ? (
              kapatmaAcik ? (
                <Buton gorunum="tehlike" yukleniyor={calisiyor} onClick={() => void kapat()}>
                  Kapatmayı onayla
                </Buton>
              ) : (
                <Buton gorunum="sessiz" onClick={() => setKapatmaAcik(true)}>
                  İki adımlı doğrulamayı kapat
                </Buton>
              )
            ) : (
              /*
               * REQUIRED politikada kapatma dugmesi HIC cizilmiyor ve
               * sebebi yaziliyor. Cizip sunucuya reddettirmek, sebebi
               * anlamayan bir kullaniciyi tekrar tekrar denemeye
               * iterdi.
               */
              <p className="text-notr-600 text-xs">
                Hesabında iki adımlı doğrulama zorunlu; kendin kapatamazsın.
              </p>
            )}
          </div>
        </Kutu>
      ) : (
        <Kutu>
          <p className="text-notr-600 text-sm">Şu an kapalı.</p>
          {/*
            ZORUNLULUK CÜMLESİ — "Şu an kapalı"nın HEMEN ALTINDA.

            Sunucu `gecisKalanGun` üretiyor ve kendi yorumunda "sayaç
            gösterilir" diyor; ölçüldü (05.09.2026), öğrenci
            uygulamasında hiçbir yerde kullanılmıyordu. Rolüne
            zorunluluk gelen öğrenci yedi günlük pencereyi hiç
            görmüyor, süre dolunca da ekran hâlâ "Şu an kapalı" diyor.

            Ton `uyari`, `hata` değil: bu bir arıza değil, bir süre.
            Kutu beyaz (`bg-notr-0`) olduğu için `uyari-600` burada
            4,96 ile geçiyor — sayfa zemininde geçmezdi (C14.23).
          */}
          {zorunluluk && (
            <p
              role="status"
              className="rounded-alan bg-uyari-500/15 text-uyari-600 mt-2 px-3 py-2 text-sm font-medium"
            >
              {zorunluluk}
            </p>
          )}
          {/*
           * "Telefonumu kaybedersem?" sorusu ACMADAN ONCE
           * cevaplaniyor. Sonra yazsaydik, kilitlenen ogrenci o metni
           * okuyabilecegi ekrana zaten giremezdi.
           */}
          <p className="text-notr-600 mt-2 text-sm">
            Açtığında sana <strong>{YEDEK_KOD_SAYISI} yedek kod</strong> vereceğiz. Telefonunu
            kaybedersen giriş yapmanın yolu onlar; bir kâğıda yazıp saklaman gerekiyor. Hem
            telefonunu hem kodları kaybedersen koçuna söyle — hesabını yönetici sıfırlayabilir.
          </p>
          <Buton
            boyut="genis"
            yukleniyor={calisiyor}
            onClick={() => void baslat()}
            className="mt-4"
          >
            İki adımlı doğrulamayı aç
          </Buton>
        </Kutu>
      )}

      {/*
        GÜVENİLİR CİHAZLAR — G-AUT-01c kural 6.

        2FA açıkken ve ekran "olağan" hâldeyken çiziliyor. Kurulum
        adımının ya da yedek kod listesinin altına ikinci bir liste
        koymak, o anda tek bir işi olan öğrenciye ikinci bir iş
        gösterirdi: kodları kâğıda yazması gereken kişi cihaz
        listesiyle ilgilenmiyor.
      */}
      {durum.kuruldu && kurulum === null && yedekKodlar === null && <GuvenilirCihazlar />}

      {/*
        AÇIK OTURUMLAR — G-SES-03, 2FA'dan BAĞIMSIZ.

        Güvenilir cihaz bölümünün aksine bu her zaman çiziliyor: oturum
        listesi 2FA açık olmasa da dolu ve "hesabıma nereden girilmiş"
        sorusunun cevabı 2FA'ya bağlı değil. Kurulum ya da yedek kod
        adımındayken gizleniyor — o anda öğrencinin tek bir işi var.
      */}
      {kurulum === null && yedekKodlar === null && <AcikOturumlar />}

      {/* G-AUT-08a — kurulum/yedek kod adımında gizli: o anda tek iş var. */}
      {kurulum === null && yedekKodlar === null && <SifreDegistir />}
    </div>
  );
}
